import {
  Container,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PageTitle from "../../widgets/PageTitle";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const AddCredit = ({ id, rechargeType }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: {
      amount: "",
      remarks: "",
      type: "credit",
      expiryDate: null,
    },
  });

  const [submitting, setSubmitting] = useState(false);

  const transactionType = watch("type");

  /*
   * When switching to debit:
   * - expiry date is irrelevant
   * - clear the value
   * - clear any validation error
   */
  useEffect(() => {
    if (transactionType === "debit") {
      setValue("expiryDate", null);
      clearErrors("expiryDate");
    }
  }, [transactionType, setValue, clearErrors]);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      const payload = {
        amount: parseFloat(data.amount),
        remarks: data.remarks.trim(),
        type: data.type,
        rechargeType,
      };

      /*
       * Only send expiryDate for credit.
       *
       * Admin is allowed to choose:
       * - past dates
       * - today's date
       * - future dates
       */
      if (data.type === "credit") {
        payload.expiryDate = dayjs(data.expiryDate).format("YYYY-MM-DD");
      }

      const response = await axios.post(
        `${API_ENDPOINT}/credit/add-credit/${id}`,
        payload,
        getHeader()
      );

      toast.success(response.data.message);

      reset({
        amount: "",
        remarks: "",
        type: "credit",
        expiryDate: null,
      });
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <PageTitle>
        {transactionType === "credit"
          ? `Add ${rechargeType} Credit`
          : `Add ${rechargeType} Debit`}
      </PageTitle>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ mt: 3 }}
      >
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Transaction Type</InputLabel>

          <Select
            value={transactionType}
            label="Transaction Type *"
            onChange={(e) => {
              setValue("type", e.target.value, {
                shouldValidate: true,
              });
            }}
            error={Boolean(errors.type)}
          >
            <MenuItem value="credit">Credit</MenuItem>
            <MenuItem value="debit">Debit</MenuItem>
          </Select>

          {errors.type && (
            <Typography color="error" variant="caption">
              {errors.type.message}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label={
            transactionType === "credit" ? "Credit Amount" : "Debit Amount"
          }
          type="number"
          inputProps={{
            step: "0.01",
            min: "0.01",
          }}
          {...register("amount", {
            required: "Amount is required",

            min: {
              value: 0.01,
              message: "Amount must be greater than 0",
            },

            pattern: {
              value: /^\d+(\.\d{1,2})?$/,
              message: "Invalid amount format (e.g. 10.50)",
            },
          })}
          error={Boolean(errors.amount)}
          helperText={errors.amount?.message}
        />

        {transactionType === "credit" && (
          <Controller
            name="expiryDate"
            control={control}
            rules={{
              required: "Expiry date is required",
              validate: (value) =>
                value && dayjs(value).isValid()
                  ? true
                  : "Expiry date must be valid",
            }}
            render={({ field }) => (
              <DatePicker
                {...field}
                label="Expiry Date"
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    required: true,
                    error: Boolean(errors.expiryDate),
                    helperText:
                      errors.expiryDate?.message ??
                      "Past, current, and future dates are allowed",
                  },
                }}
              />
            )}
          />
        )}

        <TextField
          fullWidth
          margin="normal"
          label="Remarks"
          multiline
          rows={4}
          {...register("remarks", {
            required: "Remarks are required",

            validate: (value) =>
              value?.trim()?.length > 0 || "Remarks cannot be empty",

            maxLength: {
              value: 500,
              message: "Remarks should not exceed 500 characters",
            },
          })}
          error={Boolean(errors.remarks)}
          helperText={errors.remarks?.message}
        />

        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          loading={submitting}
        >
          {transactionType === "credit" ? "Submit Credit" : "Submit Debit"}
        </LoadingButton>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {transactionType === "credit" ? "Adding credit" : "Adding debit"} for
          user ID: {id}
        </Typography>
      </Box>
    </Container>
  );
};

export default AddCredit;
